/**
 * Toast store — minimal, dependency-free notification system.
 *
 * The app had no user-facing feedback for success/error (silent saves +
 * window.confirm). This Zustand store backs a <ToastContainer/> rendered in the
 * authenticated shell. Call `toast.success(...)` / `toast.error(...)` from
 * anywhere; auto-dismisses after a timeout.
 */

import { create } from 'zustand'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  variant: ToastVariant
  title: string
  description?: string
}

interface ToastState {
  toasts: Toast[]
  push: (t: Omit<Toast, 'id'>, durationMs?: number) => string
  dismiss: (id: string) => void
}

const DEFAULT_DURATION = 4000

let counter = 0
function nextId(): string {
  counter += 1
  return `toast-${counter}-${Date.now()}`
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (t, durationMs = DEFAULT_DURATION) => {
    const id = nextId()
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }))
    if (durationMs > 0) {
      setTimeout(() => get().dismiss(id), durationMs)
    }
    return id
  },
  dismiss: (id) =>
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
}))

/** Convenience API — `toast.success('Saved')`. */
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'success', title, description }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'error', title, description }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'info', title, description }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'warning', title, description }),
}
